import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Dialog,
  Divider,
  Stack,
  Switch,
  Typography,
} from "@mui/material";

import {
  buildConsentRecord,
  getDefaultConsentDraft,
  readConsentRecord,
  writeConsentRecord,
} from "../../utils/consent/consentStorage";
import {
  disableGoogleAnalytics,
  enableGoogleAnalytics,
  trackPageView,
} from "../../utils/analytics/googleAnalytics";
import PerformanceTelemetry from "../Analytics/PerformanceTelemetry";

const CookieConsentContext = createContext(null);

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error("useCookieConsent must be used within CookieConsentProvider");
  }
  return context;
};

const CookiePreferenceRow = ({
  title,
  description,
  checked,
  disabled = false,
  onChange,
}) => (
  <Stack
    direction="row"
    spacing={2}
    alignItems="center"
    justifyContent="space-between"
    sx={{
      py: 1.2,
      px: 1.4,
      borderRadius: "5px",
      border: "1px solid rgba(255,255,255,0.08)",
      backgroundColor: "rgba(255,255,255,0.04)",
    }}
  >
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "0.96rem" }}>
        {title}
      </Typography>
      <Typography sx={{ color: "#c3c6d4", fontSize: "0.86rem", lineHeight: 1.45, mt: 0.35 }}>
        {description}
      </Typography>
    </Box>
    <Switch
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange?.(event.target.checked)}
      sx={{
        ml: 1,
        "& .MuiSwitch-switchBase.Mui-checked": {
          color: "#121216",
        },
        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
          backgroundColor: "#ffdb42",
          opacity: 1,
        },
        "& .MuiSwitch-track": {
          backgroundColor: "rgba(255,255,255,0.28)",
          opacity: 1,
        },
      }}
    />
  </Stack>
);

const CookieBanner = ({
  onAcceptAll,
  onRefuseAll,
  onCustomize,
  title,
  description,
  details,
  customizeLabel,
  refuseLabel,
  acceptLabel,
}) => (
  <Box
    sx={{
      position: "fixed",
      left: { xs: 12, sm: 18 },
      right: { xs: 12, sm: 18 },
      bottom: { xs: "calc(74px + env(safe-area-inset-bottom))", sm: 18 },
      zIndex: 1600,
      borderRadius: "5px",
      border: "1px solid rgba(255,255,255,0.1)",
      background:
        "linear-gradient(180deg, rgba(38,38,44,0.98) 0%, rgba(17,18,24,0.98) 100%)",
      boxShadow: "0 20px 60px rgba(0,0,0,0.42)",
      p: { xs: 2, sm: 2.4 },
    }}
  >
    <Stack spacing={1.4}>
      <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: { xs: "1rem", sm: "1.05rem" } }}>
        {title}
      </Typography>
      <Typography sx={{ color: "#d7dae3", fontSize: "0.9rem", lineHeight: 1.55 }}>
        {description}
      </Typography>
      <Typography sx={{ color: "#aeb3c5", fontSize: "0.82rem", lineHeight: 1.5 }}>
        {details}
      </Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        justifyContent="flex-end"
      >
        <Button
          variant="outlined"
          onClick={onCustomize}
          sx={{
            textTransform: "none",
            borderRadius: "5px",
            color: "#fff",
            borderColor: "rgba(255,255,255,0.2)",
          }}
        >
          {customizeLabel}
        </Button>
        <Button
          variant="outlined"
          onClick={onRefuseAll}
          sx={{
            textTransform: "none",
            borderRadius: "5px",
            color: "#fff",
            borderColor: "rgba(255,255,255,0.2)",
          }}
        >
          {refuseLabel}
        </Button>
        <Button
          variant="contained"
          onClick={onAcceptAll}
          sx={{
            textTransform: "none",
            borderRadius: "5px",
            color: "#121216",
            backgroundColor: "#ffdb42",
            fontWeight: 800,
            "&:hover": {
              backgroundColor: "#ffed9f",
            },
          }}
        >
          {acceptLabel}
        </Button>
      </Stack>
    </Stack>
  </Box>
);

const CookiePreferencesDialog = ({
  open,
  draft,
  onClose,
  onChange,
  onRefuseAll,
  onSave,
  t,
}) => (
  <Dialog
    open={open}
    onClose={(_event, reason) => {
      if (reason === "backdropClick") return;
      onClose();
    }}
    fullWidth
    maxWidth="sm"
    sx={{ zIndex: 1800 }}
    BackdropProps={{
      sx: {
        zIndex: 1799,
        backgroundColor: "rgba(0,0,0,0.68)",
      },
    }}
    PaperProps={{
      sx: {
        position: "relative",
        zIndex: 1801,
        pointerEvents: "auto",
        backgroundImage: "none",
        borderRadius: "5px",
      },
      onClick: (event) => {
        event.stopPropagation();
      },
      onMouseDown: (event) => {
        event.stopPropagation();
      },
    }}
  >
    <Box sx={{ p: { xs: 2, sm: 2.5 }, backgroundColor: "#111218", borderRadius: "5px" }}>
      <Stack spacing={1.4}>
        <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: "1.08rem" }}>
          {t("cookieConsent.preferencesTitle")}
        </Typography>
        <Typography sx={{ color: "#d7dae3", fontSize: "0.9rem", lineHeight: 1.55 }}>
          {t("cookieConsent.preferencesDescription")}
        </Typography>

        <Alert
          severity="info"
          sx={{
            backgroundColor: "rgba(255,219,66,0.1)",
            color: "#fff",
            borderRadius: "5px",
            border: "1px solid rgba(255,219,66,0.2)",
            "& .MuiAlert-icon": { color: "#ffdb42" },
          }}
        >
          {t("cookieConsent.analyticsNotice")}
        </Alert>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

        <CookiePreferenceRow
          title={t("cookieConsent.necessaryTitle")}
          description={t("cookieConsent.necessaryDescription")}
          checked
          disabled
        />
        <CookiePreferenceRow
          title={t("cookieConsent.analyticsTitle")}
          description={t("cookieConsent.analyticsDescription")}
          checked={draft.analytics}
          onChange={(checked) => onChange({ ...draft, analytics: checked })}
        />
        <CookiePreferenceRow
          title={t("cookieConsent.marketingTitle")}
          description={t("cookieConsent.marketingDescription")}
          checked={draft.marketing}
          onChange={(checked) => onChange({ ...draft, marketing: checked })}
        />

        <Stack
          direction={{ xs: "column-reverse", sm: "row" }}
          spacing={1}
          justifyContent="flex-end"
          sx={{ pt: 1 }}
        >
          <Button
            variant="text"
            onClick={onClose}
            sx={{ textTransform: "none", color: "#fff" }}
          >
            {t("cookieConsent.cancel")}
          </Button>
          <Button
            variant="outlined"
            onClick={onRefuseAll}
            sx={{
              textTransform: "none",
              borderRadius: "5px",
              color: "#fff",
              borderColor: "rgba(255,255,255,0.2)",
            }}
          >
            {t("cookieConsent.refuseAll")}
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            sx={{
              textTransform: "none",
              borderRadius: "5px",
              color: "#121216",
              backgroundColor: "#ffdb42",
              fontWeight: 800,
              "&:hover": { backgroundColor: "#ffed9f" },
            }}
          >
            {t("cookieConsent.saveChoices")}
          </Button>
        </Stack>
      </Stack>
    </Box>
  </Dialog>
);

const CookieConsentProvider = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [consentRecord, setConsentRecord] = useState(() => readConsentRecord());
  const [bannerVisible, setBannerVisible] = useState(() => !readConsentRecord());
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const lastTrackedPathRef = useRef("");
  const [draft, setDraft] = useState(() => {
    const stored = readConsentRecord();
    return stored?.categories || getDefaultConsentDraft();
  });

  const openPreferences = useCallback(() => {
    setPreferencesOpen(true);
  }, []);

  const closePreferences = useCallback(() => {
    setPreferencesOpen(false);
  }, []);

  const commitConsent = useCallback((record) => {
    const normalized = buildConsentRecord(record);
    writeConsentRecord(normalized);
    setConsentRecord(normalized);
    setDraft(normalized.categories);
    setBannerVisible(false);
    setPreferencesOpen(false);
    window.dispatchEvent(new Event("qubiq-cookie-consent-updated"));
  }, []);

  const acceptAll = useCallback(() => {
    commitConsent({
      status: "accepted",
      categories: {
        necessary: true,
        analytics: true,
        marketing: false,
      },
    });
  }, [commitConsent]);

  const refuseAll = useCallback(() => {
    commitConsent({
      status: "refused",
      categories: {
        necessary: true,
        analytics: false,
        marketing: false,
      },
    });
  }, [commitConsent]);

  const saveCustom = useCallback(() => {
    commitConsent({
      status: "customized",
      categories: {
        necessary: true,
        analytics: Boolean(draft.analytics),
        marketing: Boolean(draft.marketing),
      },
    });
  }, [commitConsent, draft.analytics, draft.marketing]);

  useEffect(() => {
    window.addEventListener("qubiq-open-cookie-settings", openPreferences);
    return () => {
      window.removeEventListener("qubiq-open-cookie-settings", openPreferences);
    };
  }, [openPreferences]);

  useEffect(() => {
    let cancelled = false;
    const currentPath = `${location.pathname}${location.search}`;

    const syncAnalytics = async () => {
      if (!consentRecord?.categories?.analytics) {
        disableGoogleAnalytics();
        lastTrackedPathRef.current = "";
        return;
      }

      const enabled = await enableGoogleAnalytics(consentRecord.categories);
      if (!cancelled && enabled && lastTrackedPathRef.current !== currentPath) {
        trackPageView(currentPath);
        lastTrackedPathRef.current = currentPath;
      }
    };

    syncAnalytics();
    return () => {
      cancelled = true;
    };
  }, [consentRecord, location.pathname, location.search]);

  useEffect(() => {
    const currentPath = `${location.pathname}${location.search}`;
    if (!consentRecord?.categories?.analytics) return;
    if (lastTrackedPathRef.current === currentPath) return;
    trackPageView(currentPath);
    lastTrackedPathRef.current = currentPath;
  }, [location.pathname, location.search, consentRecord?.categories?.analytics]);

  const contextValue = useMemo(
    () => ({
      consentRecord,
      consentCategories: consentRecord?.categories || getDefaultConsentDraft(),
      hasConsentChoice: Boolean(consentRecord),
      openPreferences,
      closePreferences,
      acceptAll,
      refuseAll,
      saveCustom,
    }),
    [
      consentRecord,
      openPreferences,
      closePreferences,
      acceptAll,
      refuseAll,
      saveCustom,
    ]
  );

  return (
    <CookieConsentContext.Provider value={contextValue}>
      {children}
      <PerformanceTelemetry enabled={Boolean(consentRecord?.categories?.analytics)} />
      {bannerVisible && !preferencesOpen && (
        <CookieBanner
          onAcceptAll={acceptAll}
          onRefuseAll={refuseAll}
          onCustomize={openPreferences}
          title={t("cookieConsent.bannerTitle")}
          description={t("cookieConsent.bannerDescription")}
          details={t("cookieConsent.bannerDetails")}
          customizeLabel={t("cookieConsent.customize")}
          refuseLabel={t("cookieConsent.refuseAll")}
          acceptLabel={t("cookieConsent.acceptAll")}
        />
      )}
      <CookiePreferencesDialog
        open={preferencesOpen}
        draft={draft}
        onClose={closePreferences}
        onChange={setDraft}
        onRefuseAll={refuseAll}
        onSave={saveCustom}
        t={t}
      />
    </CookieConsentContext.Provider>
  );
};

export default CookieConsentProvider;
